import type { PartyConfig } from './config.ts';
import type { Balloon, Vec2 } from './protocol.ts';

export type BalloonSample = { x:number; y:number; z:number; vx?:number; vy?:number; vz?:number };
export const BALLOON_DIAMETER = 0.72; // Wider than the 0.57 m voxel envelope, including sway.
const FLOOR_HEIGHT = 0.72;
const PLAY_RADIUS = 3.08;
// Roomy hexagonal anchors avoid greedy random placement wasting floor space.
// This is built once; search/sorting happens only when somebody spawns a balloon.
const SPAWN_ANCHORS:Vec2[]=[];
for(let row=-5;row<=5;row++)for(let column=-4;column<=4;column++){
  const x=(column+(Math.abs(row)%2)*.5)*.84,z=row*.84*Math.sqrt(3)/2;
  if(Math.hypot(x,z)<=2.94)SPAWN_ANCHORS.push({x,z});
}

/** Keep floor balloons away from the furniture. */
export function balloonOriginNear(point:Vec2):Vec2 {
  const distance=Math.hypot(point.x,point.z),scale=distance>2.75?2.75/distance:1;
  return {x:point.x*scale,z:point.z*scale};
}

/** Analytic flight, including a nonzero starting height after a collision. */
export function sampleBalloon(balloon:Balloon,now:number,physics:PartyConfig['physics'],out:BalloonSample={x:0,y:0,z:0}):BalloonSample {
  const t=Math.max(0,(now-(balloon.releaseAt??balloon.impulseAt))/1000);
  const drag=Math.max(.15,physics.drag),travel=-Math.expm1(-drag*t)/drag;
  out.x=balloon.origin.x+balloon.vx*travel;out.z=balloon.origin.z+balloon.vz*travel;
  out.vx=balloon.vx*Math.exp(-drag*t);out.vz=balloon.vz*Math.exp(-drag*t);
  if(balloon.releaseAt!==undefined){
    out.y=(balloon.originY??FLOOR_HEIGHT)+1.8*t+.42*t*t;out.vy=1.8+.84*t;
    return out;
  }
  const distance=Math.hypot(out.x,out.z);
  if(distance>PLAY_RADIUS){out.x*=PLAY_RADIUS/distance;out.z*=PLAY_RADIUS/distance;out.vx=0;out.vz=0;}
  const gravity=Math.max(.2,physics.gravity-physics.buoyancy);
  const initialHeight=Math.max(0,(balloon.originY??FLOOR_HEIGHT)-FLOOR_HEIGHT);
  let remaining=t,velocity=balloon.vy,height=initialHeight;
  const firstHit=(velocity+Math.sqrt(velocity*velocity+2*gravity*initialHeight))/gravity;
  if(remaining<=firstHit){height=Math.max(0,initialHeight+velocity*remaining-.5*gravity*remaining*remaining);out.vy=velocity-gravity*remaining;}
  else {
    remaining-=firstHit;velocity=Math.max(0,(gravity*firstHit-velocity)*.48);height=0;out.vy=0;
    for(let bounce=0;bounce<8&&velocity>.12;bounce++){
      const duration=2*velocity/gravity;
      if(remaining<=duration){height=Math.max(0,velocity*remaining-.5*gravity*remaining*remaining);out.vy=velocity-gravity*remaining;break;}
      remaining-=duration;velocity*=.48;
    }
  }
  out.y=FLOOR_HEIGHT+height;
  return out;
}

/** Deterministic roomy spawn search. Never consume inventory when no free spot exists. */
export function freeBalloonOrigin(near:Vec2,balloons:Iterable<Balloon>,now:number,physics:PartyConfig['physics'],seed:number):Vec2|null {
  const occupied=[...balloons].filter(b=>b.releaseAt===undefined&&b.expiresAt>now).map(b=>sampleBalloon(b,now,physics));
  const gap=BALLOON_DIAMETER+.08;
  const start=balloonOriginNear(near);
  const available=(p:Vec2)=>occupied.every(b=>Math.hypot(b.x-p.x,b.z-p.z)>=gap);
  const anchors=[...SPAWN_ANCHORS].sort((a,b)=>Math.hypot(a.x-start.x,a.z-start.z)-Math.hypot(b.x-start.x,b.z-start.z));
  for(const candidate of anchors)if(available(candidate))return {...candidate};
  if(available(start))return start;
  // Moving balloons may leave room between anchors; search those remaining gaps.
  for(let i=0;i<800;i++){
    const angle=i*2.3999632297+(seed%1000)*.01;
    const radius=2.94*Math.sqrt((i+.5)/800);
    const p={x:Math.cos(angle)*radius,z:Math.sin(angle)*radius};
    if(available(p))return p;
  }
  return null;
}

/** Server-only soft contact solver. Clients receive timestamped impulses, never run this independently. */
export function separateBalloons(balloons:Iterable<Balloon>,now:number,physics:PartyConfig['physics']):Balloon[] {
  const entries=[...balloons].filter(b=>b.releaseAt===undefined&&b.expiresAt>now)
    .map(balloon=>({balloon,point:sampleBalloon(balloon,now,physics),changed:false}));
  // The list is hard-bounded by configuration (120 at most); six short position passes
  // prevent chain contacts from leaving a stack. No storage or network work is done here.
  for(let pass=0;pass<6;pass++){
    let contacts=0;
    for(let i=0;i<entries.length;i++)for(let j=i+1;j<entries.length;j++){
      const a=entries[i],b=entries[j],dy=a.point.y-b.point.y;
      if(Math.abs(dy)>=BALLOON_DIAMETER)continue;
      let dx=b.point.x-a.point.x,dz=b.point.z-a.point.z;
      const horizontal=Math.hypot(dx,dz),minimum=Math.sqrt(BALLOON_DIAMETER*BALLOON_DIAMETER-dy*dy);
      if(horizontal>=minimum-.0001)continue;
      if(horizontal<.00001){const angle=(i*47+j*71)*.1;dx=Math.cos(angle);dz=Math.sin(angle);}else{dx/=horizontal;dz/=horizontal;}
      const push=(minimum-horizontal+.004)*.5;
      a.point.x-=dx*push;a.point.z-=dz*push;b.point.x+=dx*push;b.point.z+=dz*push;
      // Remove inward velocity; small outward separation feels soft, not explosive.
      const approach=(b.point.vx!-a.point.vx!)*dx+(b.point.vz!-a.point.vz!)*dz;
      if(approach<0){const impulse=-approach*.55;a.point.vx!-=dx*impulse;a.point.vz!-=dz*impulse;b.point.vx!+=dx*impulse;b.point.vz!+=dz*impulse;}
      for(const e of[a,b]){const d=Math.hypot(e.point.x,e.point.z);if(d>PLAY_RADIUS){e.point.x*=PLAY_RADIUS/d;e.point.z*=PLAY_RADIUS/d;e.point.vx=0;e.point.vz=0;}e.changed=true;}
      contacts++;
    }
    if(!contacts)break;
  }
  const changed:Balloon[]=[];
  for(const e of entries)if(e.changed){
    Object.assign(e.balloon,{origin:{x:e.point.x,z:e.point.z},originY:e.point.y,impulseAt:now,vx:e.point.vx??0,vz:e.point.vz??0,vy:e.point.vy??0,revision:e.balloon.revision+1});
    changed.push(e.balloon);
  }
  return changed;
}
